import React, { useCallback, useContext } from "react"

import type { Appointments } from "@/lib/appointments"

// The booking stepper is deprecated. We are in the process of replacing it with a suite of caching-enabled context containers.
// Until we finish that work, the stepper manages the state of the booking process and needs to be kept up to date with any changes to that data.

export function createCtx<StateType, ActionType>(
  reducer: React.Reducer<StateType, ActionType>,
  initialState: StateType
) {
  const defaultDispatch: React.Dispatch<ActionType> = () => {
    return initialState
  } // we never actually use this
  const ctx = React.createContext({
    state: initialState,
    dispatch: defaultDispatch, // just to mock out the dispatch type and make it not optioanl
  })
  function Provider(props: React.PropsWithChildren<unknown>) {
    const [state, dispatch] = React.useReducer<
      React.Reducer<StateType, ActionType>
    >(reducer, initialState)
    return <ctx.Provider value={{ state, dispatch }} {...props} />
  }
  return [ctx, Provider] as const
}

export enum Steps {
  CHOOSE_APPOINTMENT_TYPE,
  CHOOSE_PRACTITIONER,
  AUTH_CHECK,
  REVIEW_AND_PAY,
}

export interface StateTypes {
  currentStep: number
  steps: number[]
  appointmentDetails: Appointments.AppointmentDetails
}

const initialState: StateTypes = {
  currentStep: 0,
  steps: [
    Steps.CHOOSE_APPOINTMENT_TYPE,
    Steps.CHOOSE_PRACTITIONER,
    Steps.AUTH_CHECK,
    Steps.REVIEW_AND_PAY,
  ],
  appointmentDetails: {
    address_1: ``,
    address_2: ``,
    appointmentId: undefined,
    appointmentIds: [],
    appointmentType: ``,
    appointmentTypeId: ``,
    city: ``,
    post_code: ``,
    practice: ``,
    practitioner: ``,
    practitionerId: ``,
    price_cents: 0,
    region: ``,
    specialty: ``,
    timeframe: ``,
    timeslot: ``,
    userEmail: ``,
    userFirstName: ``,
    userLastName: ``,
    userUID: ``,
  },
}

type defaultStepperState = typeof initialState

type Action =
  | { type: `SET_STEPS`; payload: Array<Steps> }
  | { type: `SET_CURRENT_STEP`; payload: number }
  | { type: `INCREMENT_CURRENT_STEP`; payload?: number }
  | { type: `DECREMENT_CURRENT_STEP`; payload?: number }
  | {
      type: `UPDATE_APPOINTMENT_TYPE`
      id: string
      name: string
      price_cents: number
    }
  | { type: `UPDATE_DETAIL`; payload: Partial<Appointments.AppointmentDetails> }
  | { type: `RESET_APPOINTMENT`; payload: string }

function reducer(
  state: defaultStepperState,
  action: Action
): defaultStepperState {
  const { currentStep, steps, appointmentDetails } = state
  switch (action.type) {
    case `SET_STEPS`:
      return {
        ...state,
        steps: action.payload,
      }
    case `INCREMENT_CURRENT_STEP`:
      return {
        ...state,
        currentStep:
          currentStep < steps.length - 1 ? currentStep + 1 : currentStep,
      }
    case `SET_CURRENT_STEP`:
      return {
        ...state,
        currentStep: action.payload,
      }
    case `DECREMENT_CURRENT_STEP`:
      return {
        ...state,
        currentStep: currentStep > 0 ? currentStep - 1 : currentStep,
      }
    case `UPDATE_DETAIL`:
      return {
        ...state,
        appointmentDetails: {
          ...appointmentDetails,
          ...action.payload,
        },
      }
    case `RESET_APPOINTMENT`:
      return {
        ...state,
        appointmentDetails: {
          ...appointmentDetails,
          practice: action.payload,
          timeslot: action.payload,
          appointmentType: action.payload,
          price_cents: 0,
          practitioner: action.payload,
          practitionerId: action.payload,
        },
      }
    case `UPDATE_APPOINTMENT_TYPE`:
      return {
        ...state,
        appointmentDetails: {
          ...appointmentDetails,
          appointmentTypeId: action.id,
          appointmentType: action.name,
          price_cents: action.price_cents,
        },
      }

    default:
      return state
  }
}

export const [ctx, BookingProvider] = createCtx(reducer, initialState)
export const BookingContext = ctx

export const useStepper = () => {
  const { state, dispatch } = useContext(BookingContext)
  const { currentStep, steps, appointmentDetails } = state

  if (!BookingContext) {
    throw new Error(`useStepper should be used inside BookingProvider`)
  }

  const incrementCurrentStep = useCallback(() => {
    dispatch({ type: `INCREMENT_CURRENT_STEP` })
  }, [dispatch])

  const setCurrentStep = useCallback(
    (newStep: number) => {
      dispatch({ type: `SET_CURRENT_STEP`, payload: newStep })
    },
    [dispatch]
  )

  const updateFormDetails = (
    payload: Partial<Appointments.AppointmentDetails>
  ): void => {
    dispatch({ type: `UPDATE_DETAIL`, payload })
  }

  const updateAppointmentType = useCallback(
    ({
      appointmentType,
    }: {
      appointmentType: Appointments.AppointmentType
    }) => {
      dispatch({
        type: `UPDATE_APPOINTMENT_TYPE`,
        id: appointmentType.id,
        name: appointmentType.attributes.name,
        price_cents: appointmentType.attributes.price_cents,
      })
    },
    [dispatch]
  )

  const resetAppointment = useCallback(() => {
    dispatch({ type: `RESET_APPOINTMENT`, payload: `` })
  }, [dispatch])

  const decrementCurrentStep = useCallback(() => {
    dispatch({ type: `DECREMENT_CURRENT_STEP` })
  }, [dispatch])

  const setSteps = useCallback(
    (steps: number[]) => {
      return dispatch({ type: `SET_STEPS`, payload: steps })
    },
    [dispatch]
  )

  return {
    appointmentDetails,
    currentStep,
    decrementCurrentStep,
    incrementCurrentStep,
    resetAppointment,
    setCurrentStep,
    setSteps,
    steps,
    updateAppointmentType,
    updateFormDetails,
  }
}
